import { FC, useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Alert, Button, Card } from 'react-daisyui';
import { Link } from 'react-router-dom';
import { isValidPhoneNumber } from 'libphonenumber-js';
import Routes from '@/router/Routes.ts';
import Modal from '@/components/modals/Modal/Modal.tsx';
import useDeleteAccount from '@/api/useDeleteAccount.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import MultiSelectInput from '@/components/functional/MultiSelectInput/MultiSelectInput.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import useSaveFamilyOnboarding from '@/api/useSaveFamilyOnboarding.ts';
import useFamilyOnboarding from '@/api/useFamilyOnboarding.ts';
import useBusinessOnboarding from '@/api/useBusinessOnboarding.ts';
import useSaveBusinessOnboarding from '@/api/useSaveBusinessOnboarding.ts';
import useUpdateUserProfile from '@/api/useUpdateUserProfile.ts';
import supabaseClient from '@/api/supabaseClient.ts';
import TextArea from '@/components/functional/TextArea/TextArea.tsx';
import useStorage from '@/api/useStorage.ts';
import Calendar from '@/components/shared/Calendar/Calendar.tsx';
import { CARE_TYPE_OPTIONS, ALL_SKILLS } from '@/constants/careSkills.ts';
import { isValidUkPostcode, normaliseUkPostcode } from '@/utils/postcodeDistance.ts';
import { genderOptions } from '$/requests/RegisterUser.ts';

const genderSelectOptions: { value: string; label: string }[] = genderOptions.map((value) => ({
  value,
  label:
    value === 'prefer_not_to_say' ? 'Prefer not to say' : value.charAt(0).toUpperCase() + value.slice(1),
}));

const personalDetailsSchema = z.object({
  first_name: z.string().min(1, { message: 'Required' }),
  last_name: z.string().min(1, { message: 'Required' }),
  gender: z.string().optional(),
  phone_number: z
    .string()
    .min(1, { message: 'Required' })
    .refine((value) => isValidPhoneNumber(value), {
      message: 'Please specify a valid phone number (include the international prefix).',
    }),
  city_town: z.string().optional(),
  postcode: z
    .string()
    .min(1, { message: 'Required' })
    .transform((value) => normaliseUkPostcode(value))
    .refine((value) => isValidUkPostcode(value), { message: 'Enter a valid UK postcode' }),
});

const familySchema = personalDetailsSchema.extend({
  who_needs_care: z.string().min(1, { message: 'Required' }),
  care_type: z.string().min(1, { message: 'Required' }),
  frequency: z.string().min(1, { message: 'Required' }),
  urgency: z.string().min(1, { message: 'Required' }),
  start_type: z.enum(['ongoing', 'one-off'], { required_error: 'Required' }),
  start_date: z.string().optional(),
  location: z
    .string()
    .min(1, { message: 'Required' })
    .transform((v) => normaliseUkPostcode(v))
    .refine((v) => isValidUkPostcode(v), { message: 'Enter a valid UK postcode' }),
  hourly_budget: z.coerce.number().optional(),
});

type FamilySettingsForm = z.infer<typeof familySchema>;

const businessSchema = personalDetailsSchema.extend({
  photo_url: z.string().optional(),
  bio: z.string().optional(),
  care_types: z.array(z.string()).optional(),
  skills: z.array(z.string()).optional(),
  hourly_rate: z.coerce.number().positive({ message: 'Must be a positive number' }),
  service_area: z.string().min(1, { message: 'Required' }),
  availability_dates: z.any().optional(),
});

type BusinessSettingsForm = z.infer<typeof businessSchema>;

const whoNeedsCareOptions = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'other', label: 'Other' },
];

const familyCareTypeOptions = [
  { value: 'personal-care', label: 'Personal care' },
  { value: 'household-help', label: 'Household help' },
  { value: 'companionship', label: 'Companionship' },
  { value: 'other', label: 'Other' },
];

const frequencyOptions = [
  { value: 'hours-day', label: 'Hours per day' },
  { value: 'days-week', label: 'Days per week' },
];

const urgencyOptions = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'soon', label: 'Soon' },
  { value: 'flexible', label: 'Flexible' },
];

const careTypeOptions = CARE_TYPE_OPTIONS;
const skillOptions = ALL_SKILLS;

const SettingsPage: FC = () => {
  const { t } = useTranslation();
  const { session, logout, currentUser } = useAuthContext();
  const email = session?.user?.email ?? '';
  
  // Check if settings were unlocked in this session
  const getUnlockedState = () => {
    try {
      const unlocked = sessionStorage.getItem('settings_unlocked');
      const timestamp = sessionStorage.getItem('settings_unlocked_time');
      if (unlocked === 'true' && timestamp) {
        const unlockTime = parseInt(timestamp, 10);
        const now = Date.now();
        // Unlock expires after 30 minutes
        if (now - unlockTime < 30 * 60 * 1000) {
          return true;
        }
        // Clear expired unlock
        sessionStorage.removeItem('settings_unlocked');
        sessionStorage.removeItem('settings_unlocked_time');
      }
    } catch (e) {
      console.error('Error reading unlock state:', e);
    }
    return false;
  };

  const [isUnlocked, setIsUnlocked] = useState(getUnlockedState());
  const [authEmail, setAuthEmail] = useState(email ?? '');
  const [authPassword, setAuthPassword] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResponsibilityModalOpen, setIsResponsibilityModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Business Provider specific states
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [selectedCareTypes, setSelectedCareTypes] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [careTypesOpen, setCareTypesOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const storage = useStorage('avatars');

  // Determine user role
  const isBusinessProvider = useMemo(() => currentUser?.role === 'business_provider', [currentUser]);
  const isAdmin = useMemo(() => currentUser?.role === 'admin', [currentUser]);

  // Family Member form
  const familyForm = useForm<FamilySettingsForm>({
    resolver: zodResolver(familySchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      gender: '',
      phone_number: '',
      city_town: '',
      postcode: '',
      who_needs_care: '',
      care_type: '',
      frequency: '',
      urgency: '',
      start_type: 'ongoing',
      start_date: '',
      location: '',
      hourly_budget: '' as any,
    },
  });

  // Business Provider form
  const businessForm = useForm<BusinessSettingsForm>({
    resolver: zodResolver(businessSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      gender: '',
      phone_number: '',
      city_town: '',
      postcode: '',
      photo_url: '',
      bio: '',
      care_types: [],
      skills: [],
      hourly_rate: '' as any,
      service_area: '',
      availability_dates: undefined,
    },
  });

  // Use appropriate form based on role
  const { handleSubmit } = isBusinessProvider ? businessForm : familyForm;

  // Fetch data based on role
  const { data: familyData, isLoading: familyLoading, isError: familyError, error: familyErrorMsg, refetch: refetchFamily } = 
    useFamilyOnboarding(email, isUnlocked && !isBusinessProvider && !isAdmin);
  
  const { data: businessData, isLoading: businessLoading, isError: businessError, error: businessErrorMsg, refetch: refetchBusiness } = 
    useBusinessOnboarding(email, isUnlocked && isBusinessProvider && !isAdmin);

  // Save mutations based on role
  const { mutateAsync: saveFamilyOnboarding, isPending: familyPending, isSuccess: familySuccess, error: familySaveError, isError: familySaveErrorFlag } =
    useSaveFamilyOnboarding();
  
  const { mutateAsync: saveBusinessOnboarding, isPending: businessPending, isSuccess: businessSuccess, error: businessSaveError, isError: businessSaveErrorFlag } =
    useSaveBusinessOnboarding();

  const {
    mutateAsync: deleteAccount,
    isPending: isDeletingAccount,
    isError: isDeleteAccountError,
    error: deleteAccountError,
  } = useDeleteAccount();

  const {
    mutateAsync: updateUserProfile,
    isPending: isProfilePending,
    isError: isProfileSaveError,
    error: profileSaveError,
  } = useUpdateUserProfile();

  // Combine states based on role
  const isLoading = isBusinessProvider ? businessLoading : familyLoading;
  const isError = isBusinessProvider ? businessError : familyError;
  const error = isBusinessProvider ? businessErrorMsg : familyErrorMsg;
  const isPending = (isBusinessProvider ? businessPending : familyPending) || isProfilePending;
  const isSuccess = isBusinessProvider ? businessSuccess : familySuccess;
  const saveError = (isBusinessProvider ? businessSaveError : familySaveError) ?? profileSaveError;
  const isSaveError = (isBusinessProvider ? businessSaveErrorFlag : familySaveErrorFlag) || isProfileSaveError;
  const refetch = isBusinessProvider ? refetchBusiness : refetchFamily;

  const handleDateSelect = (date: Date, isStart: boolean) => {
    if (isStart) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (startDate && date >= startDate) {
        setEndDate(date);
      }
    }
  };

  const personalDetailsDefaults = useMemo(
    () => ({
      first_name: currentUser?.first_name ?? '',
      last_name: currentUser?.last_name ?? '',
      gender: currentUser?.gender ?? '',
      phone_number: currentUser?.phone_number ?? '',
      city_town: currentUser?.city_town ?? '',
      postcode: currentUser?.postcode ?? '',
    }),
    [currentUser]
  );

  useEffect(() => {
    if (isBusinessProvider && businessData) {
      businessForm.reset({
        ...personalDetailsDefaults,
        photo_url: businessData.photo_url ?? '',
        bio: businessData.bio ?? '',
        care_types: businessData.care_types ?? [],
        skills: businessData.skills ?? [],
        hourly_rate: businessData.hourly_rate ?? ('' as any),
        service_area: businessData.service_area ?? '',
        availability_dates: businessData.availability_dates ?? undefined,
      });
      setSelectedCareTypes(businessData.care_types ?? []);
      setSelectedSkills(businessData.skills ?? []);

      // Initialize availability dates
      if (businessData.availability_dates?.startDate && businessData.availability_dates?.endDate) {
        setStartDate(new Date(businessData.availability_dates.startDate));
        setEndDate(new Date(businessData.availability_dates.endDate));
      } else {
        // Clear dates if no availability data
        setStartDate(null);
        setEndDate(null);
      }
    } else if (!isBusinessProvider && familyData) {
      familyForm.reset({
        ...personalDetailsDefaults,
        who_needs_care: familyData.who_needs_care ?? '',
        care_type: familyData.care_type ?? '',
        frequency: familyData.frequency ?? '',
        urgency: familyData.urgency ?? '',
        start_type: (familyData.start_type as FamilySettingsForm['start_type']) ?? 'ongoing',
        start_date: familyData.start_date ?? '',
        location: familyData.location ?? '',
        hourly_budget: familyData.hourly_budget ?? ('' as any),
      });
    }
  }, [isBusinessProvider, businessData, familyData, personalDetailsDefaults]);

  const onSubmit = async (form: FamilySettingsForm | BusinessSettingsForm) => {
    if (!session?.user?.id) return;

    await updateUserProfile({
      user_id: session.user.id,
      first_name: form.first_name,
      last_name: form.last_name,
      gender: form.gender,
      phone_number: form.phone_number,
      city_town: form.city_town,
      postcode: form.postcode,
    });

    if (isBusinessProvider) {
      const businessFormValues = form as BusinessSettingsForm;
      const businessOnboardingForm = {
        photo_url: businessFormValues.photo_url,
        bio: businessFormValues.bio,
        care_types: businessFormValues.care_types,
        skills: businessFormValues.skills,
        hourly_rate: businessFormValues.hourly_rate,
        service_area: businessFormValues.service_area,
        availability_dates: businessFormValues.availability_dates,
      };
      let photoUrl = businessOnboardingForm.photo_url;

      // Upload photo if selected
      if (photoFile && session?.user?.id) {
        try {
          const fileName = `${session.user.id}_${Date.now()}.${photoFile.name.split('.').pop()}`;
          await storage.store.mutateAsync({
            file: photoFile,
            path: fileName,
          });
          photoUrl = storage.getPublicUrl(fileName);
        } catch (error) {
          console.error('Error uploading photo:', error);
        }
      }

      const availabilityData = startDate && endDate ? {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      } : undefined;

      await saveBusinessOnboarding({
        email,
        ...businessOnboardingForm,
        photo_url: photoUrl,
        care_types: selectedCareTypes,
        skills: selectedSkills,
        availability_dates: availabilityData,
      });
    } else {
      const familyFormValues = form as FamilySettingsForm;
      const familyOnboardingForm = {
        who_needs_care: familyFormValues.who_needs_care,
        care_type: familyFormValues.care_type,
        frequency: familyFormValues.frequency,
        urgency: familyFormValues.urgency,
        start_type: familyFormValues.start_type,
        start_date: familyFormValues.start_date,
        location: familyFormValues.location,
        hourly_budget: familyFormValues.hourly_budget,
      };
      await saveFamilyOnboarding({
        email,
        ...familyOnboardingForm,
      });
    }
  };

  const handleDeleteAccount = async () => {
    if (isDeletingAccount || !email) return;
    try {
      await deleteAccount(email);
      await supabaseClient.auth.signOut();
      setIsDeleteModalOpen(false);
    } catch (e) {
      // errors are surfaced via isDeleteAccountError && deleteAccountError
      console.error(e);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 w-full overflow-x-hidden">
      {!isAdmin && (
        <div className="flex flex-col gap-2 w-full">
          <h1 className="text-2xl font-semibold">{t('settingsPage.title', 'Edit your profile')}</h1>
          <p className="text-sm text-gray-500">
            To update your profile, email{' '}
            <a href="mailto:support@caretailor.co.uk" className="text-blue-600 hover:underline font-medium">
              support@caretailor.co.uk
            </a>
          </p>
        </div>
      )}

      {!isAdmin && !isUnlocked && (
        <Card className="shadow-lg max-w-[640px] w-full bg-[#f7f7f7]">
          <Card.Body className="gap-4 p-4 sm:p-6">
            <div className="flex flex-col gap-3">
              <TextInput
                label={t('settingsPage.emailLabel', 'Email')}
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
              />
              <TextInput
                label={t('label.password')}
                type="password"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
              />
              {unlockError && <Alert status="error">{unlockError}</Alert>}
              <Button
                color="ghost"
                className="bg-black text-white hover:opacity-90"
                onClick={async () => {
                  try {
                    setUnlockError(null);
                    const { error } = await supabaseClient.auth.signInWithPassword({
                      email: authEmail,
                      password: authPassword,
                    });
                    if (error) {
                      setUnlockError(t('settingsPage.invalidCredentials', 'Invalid email or password.'));
                      return;
                    }
                    // Save unlock state to sessionStorage
                    try {
                      sessionStorage.setItem('settings_unlocked', 'true');
                      sessionStorage.setItem('settings_unlocked_time', Date.now().toString());
                    } catch (e) {
                      console.error('Error saving unlock state:', e);
                    }
                    setIsUnlocked(true);
                    void refetch();
                  } catch (e) {
                    setUnlockError((e as Error).message);
                  }
                }}
              >
                {t('settingsPage.editProfile', 'Edit profile')}
              </Button>
              <Button className="bg-white text-black border border-gray-200" onClick={logout}>
                {t('button.logout')}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {!isAdmin && isUnlocked && (
        <Card className="shadow-lg max-w-[640px] w-full">
          <Card.Body className="gap-4 p-4 sm:p-6">
            {isError && <Alert status="error">{error?.message}</Alert>}
            {isSaveError && <Alert status="error">{saveError?.message}</Alert>}
            {isSuccess && (
              <Alert status="success" className="bg-[#FED32C] text-black border-none">
                {t('settingsPage.success', 'Saved successfully')}
              </Alert>
            )}
            <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-3">
                <div>
                  <p className="text-sm text-text-secondary">{t('settingsPage.emailLabel', 'Email')}</p>
                  <p className="font-medium">{email || '-'}</p>
                </div>

                {isBusinessProvider ? (
                  // Business Provider form fields
                  <>
                    {/* Personal details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Controller
                        control={businessForm.control}
                        name="first_name"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            label={t('label.firstName')}
                            required
                            placeholder={t('placeholder.firstName')}
                            error={errors.first_name?.message}
                          />
                        )}
                      />
                      <Controller
                        control={businessForm.control}
                        name="last_name"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            label={t('label.lastName')}
                            required
                            placeholder={t('placeholder.lastName')}
                            error={errors.last_name?.message}
                          />
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Controller
                        control={businessForm.control}
                        name="gender"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <SelectInput
                            value={value ?? ''}
                            onChange={onChange}
                            options={genderSelectOptions}
                            label={t('label.gender')}
                            placeholder={t('placeholder.gender')}
                            error={errors.gender?.message}
                          />
                        )}
                      />
                      <Controller
                        control={businessForm.control}
                        name="phone_number"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            type="tel"
                            autoComplete="tel"
                            label={t('label.phoneNumber')}
                            required
                            placeholder={t('placeholder.phoneNumber')}
                            error={errors.phone_number?.message}
                          />
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Controller
                        control={businessForm.control}
                        name="city_town"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            label={t('label.cityTown')}
                            placeholder={t('placeholder.cityTown')}
                            error={errors.city_town?.message}
                          />
                        )}
                      />
                      <Controller
                        control={businessForm.control}
                        name="postcode"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={(e) => onChange(e.target.value.toUpperCase())}
                            label={t('label.postcode')}
                            required
                            placeholder={t('placeholder.postcode')}
                            error={errors.postcode?.message}
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                          />
                        )}
                      />
                    </div>

                    {/* Photo upload */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setPhotoFile(file);
                      }}
                      accept="image/*"
                      className="hidden"
                    />
                    {!photoFile && !businessData?.photo_url ? (
                      <Button
                        type="button"
                        color="ghost"
                        className="border border-gray-300"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        + Upload photo
                      </Button>
                    ) : (
                      <div className="relative">
                        <img
                          src={photoFile ? URL.createObjectURL(photoFile) : (businessData?.photo_url || '')}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="absolute top-2 right-2 flex gap-2">
                          <button
                            type="button"
                            className="w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-100 border border-gray-200"
                            onClick={() => fileInputRef.current?.click()}
                            title="Change photo"
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-100 border border-gray-200 text-error"
                            onClick={() => {
                              setPhotoFile(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                            title="Remove photo"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Bio */}
                    <Controller
                      control={businessForm.control}
                      name="bio"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <TextArea
                          value={value ?? ''}
                          onChange={onChange}
                          placeholder="Add your bio..."
                          error={errors.bio?.message}
                          rows={5}
                        />
                      )}
                    />

                    {/* Care Types */}
                    <MultiSelectInput
                      value={selectedCareTypes}
                      onChange={setSelectedCareTypes}
                      options={careTypeOptions}
                      placeholder="Type of service"
                      label="Care Types"
                      isOpen={careTypesOpen}
                      onToggle={() => { setCareTypesOpen((o) => !o); setSkillsOpen(false); }}
                      onClose={() => setCareTypesOpen(false)}
                    />

                    {/* Skills */}
                    <MultiSelectInput
                      value={selectedSkills}
                      onChange={setSelectedSkills}
                      options={skillOptions}
                      placeholder="Skills"
                      label="Skills"
                      isOpen={skillsOpen}
                      onToggle={() => { setSkillsOpen((o) => !o); setCareTypesOpen(false); }}
                      onClose={() => setSkillsOpen(false)}
                    />

                    {/* Hourly Rate */}
                    <Controller
                      control={businessForm.control}
                      name="hourly_rate"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <TextInput
                          type="number"
                          value={value ?? ''}
                          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                          placeholder="Hourly rate"
                          error={errors.hourly_rate?.message}
                        />
                      )}
                    />

                    {/* Service Area */}
                    <Controller
                      control={businessForm.control}
                      name="service_area"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <TextInput
                          value={value}
                          onChange={onChange}
                          placeholder="e.g. HP11, SW1, M1"
                          label="Service area (first 3–4 characters of your postcode)"
                          required
                          error={errors.service_area?.message}
                          maxLength={10}
                        />
                      )}
                    />

                    {/* Availability Calendar */}
                    <div className="flex flex-col gap-3">
                      <div>
                        <label className="text-sm font-medium">Availability</label>
                        <p className="text-xs text-gray-500 mt-1">Select your available date range</p>
                      </div>

                        <Calendar
                          startDate={startDate}
                          endDate={endDate}
                          onDateSelect={handleDateSelect}
                        />

                     
                      {(!startDate || !endDate) && (
                        <p className="text-xs text-gray-400 italic">
                          No availability set. Click a start date and an end date to set your availability.
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  // Family Member form fields
                  <>
                    {/* Personal details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Controller
                        control={familyForm.control}
                        name="first_name"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            label={t('label.firstName')}
                            required
                            placeholder={t('placeholder.firstName')}
                            error={errors.first_name?.message}
                          />
                        )}
                      />
                      <Controller
                        control={familyForm.control}
                        name="last_name"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            label={t('label.lastName')}
                            required
                            placeholder={t('placeholder.lastName')}
                            error={errors.last_name?.message}
                          />
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Controller
                        control={familyForm.control}
                        name="gender"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <SelectInput
                            value={value ?? ''}
                            onChange={onChange}
                            options={genderSelectOptions}
                            label={t('label.gender')}
                            placeholder={t('placeholder.gender')}
                            error={errors.gender?.message}
                          />
                        )}
                      />
                      <Controller
                        control={familyForm.control}
                        name="phone_number"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            type="tel"
                            autoComplete="tel"
                            label={t('label.phoneNumber')}
                            required
                            placeholder={t('placeholder.phoneNumber')}
                            error={errors.phone_number?.message}
                          />
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Controller
                        control={familyForm.control}
                        name="city_town"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            label={t('label.cityTown')}
                            placeholder={t('placeholder.cityTown')}
                            error={errors.city_town?.message}
                          />
                        )}
                      />
                      <Controller
                        control={familyForm.control}
                        name="postcode"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={(e) => onChange(e.target.value.toUpperCase())}
                            label={t('label.postcode')}
                            required
                            placeholder={t('placeholder.postcode')}
                            error={errors.postcode?.message}
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                          />
                        )}
                      />
                    </div>

                    <Controller
                      control={familyForm.control}
                      name="who_needs_care"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <SelectInput
                          value={value}
                          onChange={onChange}
                          options={whoNeedsCareOptions}
                          placeholder={t('onboardingPage.whoNeedsCarePlaceholder')}
                          error={errors.who_needs_care?.message}
                        />
                      )}
                    />
                    <Controller
                      control={familyForm.control}
                      name="care_type"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <SelectInput
                          value={value}
                          onChange={onChange}
                          options={familyCareTypeOptions}
                          placeholder={t('onboardingPage.careTypePlaceholder')}
                          error={errors.care_type?.message}
                        />
                      )}
                    />
                    <Controller
                      control={familyForm.control}
                      name="frequency"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <SelectInput
                          value={value}
                          onChange={onChange}
                          options={frequencyOptions}
                          placeholder={t('onboardingPage.frequencyPlaceholder')}
                          error={errors.frequency?.message}
                        />
                      )}
                    />
                    <Controller
                      control={familyForm.control}
                      name="urgency"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <SelectInput
                          value={value}
                          onChange={onChange}
                          options={urgencyOptions}
                          placeholder={t('onboardingPage.urgencyPlaceholder')}
                          error={errors.urgency?.message}
                        />
                      )}
                    />
                    <Controller
                      control={familyForm.control}
                      name="start_type"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <SelectInput
                          value={value}
                          onChange={onChange}
                          options={[
                            { value: 'ongoing', label: t('onboardingPage.startTypeOngoing') },
                            { value: 'one-off', label: t('onboardingPage.startTypeOneOff') },
                          ]}
                          placeholder={t('onboardingPage.startTypePlaceholder')}
                          error={errors.start_type?.message}
                        />
                      )}
                    />
                    {familyForm.watch('start_type') === 'one-off' && (
                      <Controller
                        control={familyForm.control}
                        name="start_date"
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                          <TextInput
                            value={value}
                            onChange={onChange}
                            placeholder={t('onboardingPage.startDatePlaceholder')}
                            error={errors.start_date?.message}
                          />
                        )}
                      />
                    )}
                    <Controller
                      control={familyForm.control}
                      name="location"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <TextInput
                          value={value}
                          onChange={(e) => onChange(e.target.value.toUpperCase())}
                          placeholder={t('onboardingPage.locationPlaceholder')}
                          label={t('onboardingPage.locationLabel')}
                          required
                          error={errors.location?.message}
                          maxLength={10}
                          autoCapitalize="characters"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      )}
                    />
                    <Controller
                      control={familyForm.control}
                      name="hourly_budget"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <TextInput
                          type="number"
                          value={value ?? ''}
                          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                          placeholder={t('onboardingPage.budgetPlaceholder')}
                          error={errors.hourly_budget?.message}
                        />
                      )}
                    />
                  </>
                )}
              </div>
              <LoadingButton type="submit" color="primary" loading={isPending || isLoading}>
                {t('settingsPage.save', 'Save')}
              </LoadingButton>
            </form>
          </Card.Body>
        </Card>
      )}

      {isAdmin && (
        <div className="flex flex-col items-center gap-4 w-full max-w-[640px]">
          <Card className="shadow-lg w-full">
            <Card.Body className="gap-4 p-4 sm:p-6">
              <div>
                <p className="text-sm text-text-secondary">{t('settingsPage.emailLabel', 'Email')}</p>
                <p className="font-medium">{email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-text-secondary">Role</p>
                <p className="font-medium">Admin</p>
              </div>
            </Card.Body>
          </Card>
          <Button className="bg-black text-white border-none hover:opacity-90 w-full" onClick={logout}>
            {t('button.logout')}
          </Button>
        </div>
      )}

      {!isAdmin && (
        <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 w-full mb-6">
          <Link to={Routes.TERMS} className="w-full sm:w-auto">
            <Button className="bg-black text-white border-none hover:opacity-90 min-w-[200px] w-full sm:w-auto">
              Terms & Conditions
            </Button>
          </Link>
          <Link to={Routes.PRIVACY_POLICY} className="w-full sm:w-auto">
            <Button className="bg-black text-white border-none hover:opacity-90 min-w-[200px] w-full sm:w-auto">
              Privacy & Policy
            </Button>
          </Link>
          <Button
            className="bg-black text-white border-none hover:opacity-90 min-w-[200px] w-full sm:w-auto"
            onClick={() => setIsResponsibilityModalOpen(true)}
          >
            Responsibility notice
          </Button>
          <Button
            className="bg-black text-white border-none hover:opacity-90 min-w-[200px] w-full sm:w-auto"
            onClick={() => setIsContactModalOpen(true)}
          >
            Contact us
          </Button>
          {isUnlocked && (
            <Button
              className="bg-white text-error border border-error hover:bg-error hover:text-white min-w-[200px] w-full sm:w-auto"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              {t('settingsPage.deleteAccount', 'Delete Account')}
            </Button>
          )}
        </div>
      )}

      {/* Responsibility Notice Modal */}
      <Modal.Wrapper
        isOpen={isResponsibilityModalOpen}
        onClose={() => setIsResponsibilityModalOpen(false)}
        showCloseButton
        closeable
        className="w-full max-w-[560px]"
      >
        <Modal.Title>
          <h3 className="text-lg font-semibold">Responsibility Notice</h3>
        </Modal.Title>
        <div className="text-sm text-gray-700 space-y-4 pb-2">
          <p>
            CareTailor Ltd is an introductory platform that connects families and individuals seeking care with
            independent carers. We do not employ carers, provide care services, or act as a care agency.
          </p>
          <p>
            All care arrangements — including rates, schedules, and working conditions — are agreed directly between
            the client and the carer. CareTailor Ltd is not a party to any such agreement and accepts no liability
            for the conduct, competence, or suitability of any carer or client on the platform.
          </p>
          <p>
            Clients are solely responsible for carrying out their own due diligence before engaging a carer,
            including — but not limited to — verifying identity, checking references, and satisfying themselves
            that any carer is suitable for their specific care needs.
          </p>
          <p>
            Carers are self-employed and solely responsible for the services they provide, their own insurance,
            compliance with applicable laws, and the safety and quality of the care they deliver.
          </p>
          <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
            CareTailor - fixing care starts here<br />
            Share it. Tell Others. Join the waitlist.<br />
            <a href="https://www.caretailor.co.uk" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">www.caretailor.co.uk</a>
          </p>
        </div>
        <Modal.Nav>
          {({ handleClose }) => (
            <Button
              className="w-full bg-black text-white border-none hover:opacity-90"
              onClick={handleClose}
            >
              Close
            </Button>
          )}
        </Modal.Nav>
      </Modal.Wrapper>

      {/* Contact Us Modal */}
      <Modal.Wrapper
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        showCloseButton
        closeable
        className="w-full max-w-[480px]"
      >
        <Modal.Title>
          <h3 className="text-lg font-semibold">Contact Us</h3>
        </Modal.Title>
        <div className="text-sm text-gray-700 space-y-4 pb-2">
          <p>
            We&apos;re here to help. If you have any questions, concerns, or feedback, please don&apos;t hesitate
            to get in touch with the CareTailor team.
          </p>
          <div className="space-y-2">
            <div>
              <p className="font-medium text-black">Email</p>
              <a href="mailto:support@caretailor.co.uk" className="text-blue-600 hover:underline">
                support@caretailor.co.uk
              </a>
            </div>
            <div>
              <p className="font-medium text-black">Registered address</p>
              <p className="text-gray-600">20 Wenlock Road, London, England, N1 7GU</p>
            </div>
            <div>
              <p className="font-medium text-black">Company number</p>
              <p className="text-gray-600">16534918</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
            CareTailor - fixing care starts here<br />
            Share it. Tell Others. Join the waitlist.<br />
            <a href="https://www.caretailor.co.uk" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">www.caretailor.co.uk</a>
          </p>
        </div>
        <Modal.Nav>
          {({ handleClose }) => (
            <Button
              className="w-full bg-black text-white border-none hover:opacity-90"
              onClick={handleClose}
            >
              Close
            </Button>
          )}
        </Modal.Nav>
      </Modal.Wrapper>

      {/* Delete Account Modal */}
      <Modal.Wrapper
        isOpen={isDeleteModalOpen}
        onClose={() => (isDeletingAccount ? null : setIsDeleteModalOpen(false))}
        showCloseButton
        className="w-full max-w-[420px]"
      >
        <Modal.Title>
          <h3 className="text-lg font-semibold">{t('settingsPage.deleteAccount', 'Delete Account')}</h3>
        </Modal.Title>
        <p className="text-sm text-text-secondary">
          Are you sure you want to delete your account? This action cannot be undone.
        </p>
        {isDeleteAccountError && <Alert status="error">{deleteAccountError?.message}</Alert>}
        <Modal.Nav>
          {({ handleClose }) => (
            <div className="flex gap-2 w-full">
              <Button
                className="flex-1 bg-white text-black border border-gray-200 hover:bg-gray-100"
                onClick={() => {
                  if (isDeletingAccount) return;
                  handleClose();
                  setIsDeleteModalOpen(false);
                }}
                disabled={isDeletingAccount}
              >
                Cancel
              </Button>
              <LoadingButton
                className="flex-1"
                color="error"
                loading={isDeletingAccount}
                onClick={handleDeleteAccount}
              >
                Yes, delete
              </LoadingButton>
            </div>
          )}
        </Modal.Nav>
      </Modal.Wrapper>
    </div>
  );
};

export default SettingsPage;

